import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import {
  createMaxBoulesRule, deleteMaxBoulesRule, listMaxBoulesRules, updateMaxBoulesRule,
} from '../../api/maxBoules';
import './KonbinezonRatioPage.css';

// Nombre de boules (lignes de pari) par fiche. Règle opt-in : sans règle activée,
// AUCUNE limite. Règle ON -> le plafond devient `max_count`. Règle OFF (règle
// enregistrée mais décochée) -> aucune limite. Portée company (Tout) / group /
// bank — le plus spécifique gagne côté vente.

const DEFAULT_CAP = 20;
const DEFAULTS = { enabled: false, max_count: DEFAULT_CAP };

const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Banque') return { bank: scopeId };
  return {};
};

const MaxBoulesPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [ruleId, setRuleId] = useState(null);
  const [enabled, setEnabled] = useState(DEFAULTS.enabled);
  const [maxCount, setMaxCount] = useState(String(DEFAULTS.max_count));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const scopeReady = filter === 'Tout' || !!scopeId;

  const load = useCallback(() => {
    if (!scopeReady) return;
    setLoading(true);
    setError('');
    setSaved(false);
    listMaxBoulesRules(scopeParams(filter, scopeId))
      .then((rows) => {
        const rule = Array.isArray(rows) ? rows[0] : null;
        if (rule) {
          setRuleId(rule.id);
          setEnabled(rule.enabled);
          setMaxCount(String(rule.max_count));
        } else {
          setRuleId(null);
          setEnabled(DEFAULTS.enabled);
          setMaxCount(String(DEFAULTS.max_count));
        }
      })
      .catch(() => setError('Impossible de charger la règle.'))
      .finally(() => setLoading(false));
  }, [filter, scopeId, scopeReady]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    const n = parseInt(maxCount, 10);
    if (enabled && (!Number.isFinite(n) || n < 1)) {
      setError('Le nombre de boules doit être au moins 1.');
      return;
    }
    setSaving(true);
    setError('');
    setSaved(false);
    const payload = { enabled, max_count: Number.isFinite(n) && n >= 1 ? n : DEFAULT_CAP };
    try {
      if (ruleId) {
        await updateMaxBoulesRule(ruleId, payload);
      } else {
        const created = await createMaxBoulesRule({ ...payload, ...scopeParams(filter, scopeId) });
        setRuleId(created.id);
      }
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible. Vérifiez les valeurs.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!ruleId) return;
    setSaving(true);
    setError('');
    try {
      await deleteMaxBoulesRule(ruleId);
      setRuleId(null);
      setEnabled(DEFAULTS.enabled);
      setMaxCount(String(DEFAULTS.max_count));
      setSaved(false);
    } catch (err) {
      setError('Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <p className="kr-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/securite" className="bc-link">Securite</Link>
        {' >> '}
        <span className="bc-current">Nombre de boules par fiche</span>
      </p>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Nombre de boules par fiche</span>
        </div>
        <div className="kr-card-body">
          <div className="kr-radio-row">
            {['Tout', 'Groupe', 'Banque'].map((opt) => (
              <label key={opt} className="kr-radio-label">
                <input
                  type="radio"
                  name="mb-filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="kr-row">
              <label className="kr-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              )}
            </div>
          )}

        </div>
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">
            Règle {filter === 'Tout' ? 'générale (company)' : filter.toLowerCase()}
          </span>
        </div>

        {!scopeReady ? (
          <div className="kr-card-body">
            <p className="kr-hint">Choisir un {filter.toLowerCase()} ci-dessus.</p>
          </div>
        ) : loading ? (
          <div className="kr-card-body"><p>Chargement...</p></div>
        ) : (
          <form className="kr-card-body" onSubmit={handleSave}>
            <label className="kr-toggle-row">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }}
              />
              <span>Activer la règle</span>
              {!ruleId && <span className="kr-hint">(pas encore de règle pour cette portée)</span>}
            </label>

            <div className="kr-row">
              <label className="kr-label">Max boules / fiche</label>
              <input
                className="kr-input kr-input-sm"
                type="number"
                min="1"
                step="1"
                value={maxCount}
                disabled={!enabled}
                onChange={(e) => { setMaxCount(e.target.value); setSaved(false); }}
              />
              <span className="kr-hint">
                {enabled
                  ? `Une fiche ne pourra pas dépasser ${maxCount || '?'} boules.`
                  : 'Règle inactive : aucune limite sur le nombre de boules.'}
              </span>
            </div>

            <div className="kr-btn-row">
              <button type="submit" className="kr-save-btn" disabled={saving}>
                {ruleId ? 'Enregistrer' : 'Créer la règle'}
              </button>
              {ruleId && (
                <button type="button" className="kr-del-btn" onClick={handleDelete} disabled={saving}>
                  Supprimer
                </button>
              )}
            </div>

            {saved && <p className="kr-ok">Configuration enregistrée.</p>}
            {error && <p className="kr-error">{error}</p>}
          </form>
        )}
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Comment ça marche</span>
        </div>
        <div className="kr-card-body">
          <p className="kr-explain">
            Par défaut, <strong>aucune limite</strong> sur le nombre de boules
            (lignes de pari) d'une fiche. Cette règle permet d'en imposer une, à
            activer quand on veut.
          </p>
          <p className="kr-explain kr-explain-ex">
            <strong>Activée</strong> → une fiche ne peut pas dépasser la valeur
            saisie (ex : {maxCount || '20'} boules par fiche).<br />
            <strong>Désactivée</strong> (règle enregistrée mais décochée) → aucune
            limite sur cette portée.<br />
            <strong>Aucune règle</strong> → aucune limite.
          </p>
          <p className="kr-explain kr-explain-note">
            Portée la plus précise gagne : bank {'>'} group {'>'} company. Une règle
            désactivée à un niveau précis masque volontairement les niveaux
            au-dessus. Le contrôle s'applique aussi aux fiches vendues hors-ligne.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default MaxBoulesPage;
